import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Users, CalendarDays, Plus, Trash2, Send, MessageSquare, Building2, Clock, Pencil, X, List, LayoutGrid, UserPlus } from "lucide-react";
import { EditBookingDialog } from "@/components/EditBookingDialog";
import { BookingsCalendar } from "@/components/BookingsCalendar";

export default function ManagerDashboard() {
  const { api, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddMember, setShowAddMember] = useState(false);
  const [newMember, setNewMember] = useState({ name: "", email: "", phone: "" });
  const [msgForm, setMsgForm] = useState({ content: "", message_type: "note" });
  const [sending, setSending] = useState(false);
  const [rentalsView, setRentalsView] = useState("list");
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPurpose, setGuestPurpose] = useState("");
  const [regLoading, setRegLoading] = useState(false);
  const [showGuestForm, setShowGuestForm] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "manager") { navigate("/login"); return; }
    loadAll();
  }, [user, authLoading]);

  const loadAll = async () => {
    try {
      const [s, m, b, msgs, inb] = await Promise.all([
        api("get", "/manager/stats"),
        api("get", "/manager/members"),
        api("get", "/manager/bookings"),
        api("get", "/messages"),
        api("get", "/messages/inbox").catch(() => ({ data: [] })),
      ]);
      setStats(s.data);
      setMembers(m.data);
      setBookings(b.data);
      setMessages(msgs.data);
      setInbox(inb.data || []);
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  const addMember = async () => {
    if (!newMember.name || !newMember.email) {
      toast.error("Name and email are required"); return;
    }
    try {
      await api("post", "/manager/members", newMember);
      toast.success("Member added — credentials sent by email");
      setNewMember({ name: "", email: "", phone: "" });
      setShowAddMember(false);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add member"); }
  };

  const removeMember = async (id) => {
    if (!window.confirm("Remove this member?")) return;
    try {
      await api("delete", `/manager/members/${id}`);
      toast.success("Member removed");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const [editBooking, setEditBooking] = useState(null);

  const cancelBooking = async (id) => {
    if (!window.confirm("Cancel this booking?")) return;
    try {
      await api("put", `/bookings/${id}/cancel`);
      toast.success("Booking cancelled");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const sendMessage = async () => {
    if (!msgForm.content.trim()) { toast.error("Message cannot be empty"); return; }
    setSending(true);
    try {
      await api("post", "/messages", msgForm);
      toast.success("Message sent to admin");
      setMsgForm({ content: "", message_type: "note" });
      const msgs = await api("get", "/messages");
      setMessages(msgs.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); }
    finally { setSending(false); }
  };

  const registerGuest = async () => {
    if (!guestName.trim()) { toast.error("Guest name is required"); return; }
    setRegLoading(true);
    try {
      await api("post", "/guests/pre-register", {
        name: guestName.trim(),
        email: guestEmail.trim() || null,
        purpose: guestPurpose.trim() || "Visit",
      });
      toast.success(`${guestName.trim()} has been pre-registered${guestEmail.trim() ? " and will receive a check-in QR code" : ""}`);
      setGuestName(""); setGuestEmail(""); setGuestPurpose(""); setShowGuestForm(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setRegLoading(false); }
  };

  if (loading) return <div className="max-w-5xl mx-auto px-6 py-12"><div className="h-64 bg-muted animate-pulse rounded-xl" /></div>;

  const statusColor = (s) => s === "confirmed" ? "bg-green-100 text-green-700" : s === "pending_payment" ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-700";

  return (
    <div className="max-w-5xl mx-auto px-6 py-12 animate-fade-in" data-testid="manager-dashboard">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Building2 className="h-6 w-6 text-orange-500" />
            <h1 className="font-heading font-bold text-3xl">{stats?.company_name || "Company"}</h1>
          </div>
          <p className="text-muted-foreground">Manager Dashboard</p>
        </div>
        <Button variant="outline" className="rounded-none" onClick={() => setShowGuestForm(v => !v)} data-testid="mgr-register-guest-btn">
          <UserPlus className="h-4 w-4 mr-2" />Register Guest
        </Button>
      </div>

      {showGuestForm && (
        <Card className="p-5 border mb-6" data-testid="mgr-guest-form">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-semibold text-sm flex items-center gap-2"><UserPlus className="h-4 w-4" /> Pre-register a Guest</h3>
            <Button size="sm" variant="ghost" onClick={() => setShowGuestForm(false)}><X className="h-4 w-4" /></Button>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Guests with an email will receive a QR code for quick check-in at the lobby.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label className="text-xs">Name *</Label><Input value={guestName} onChange={e => setGuestName(e.target.value)} placeholder="Guest name" data-testid="mgr-guest-name" /></div>
            <div><Label className="text-xs">Email</Label><Input value={guestEmail} onChange={e => setGuestEmail(e.target.value)} placeholder="Email (optional)" data-testid="mgr-guest-email" /></div>
            <div><Label className="text-xs">Purpose</Label><Input value={guestPurpose} onChange={e => setGuestPurpose(e.target.value)} placeholder="e.g. Meeting, Delivery" data-testid="mgr-guest-purpose" /></div>
          </div>
          <Button className="mt-3 bg-foreground text-background rounded-none" onClick={registerGuest} disabled={regLoading} data-testid="mgr-submit-guest">
            {regLoading ? "Registering..." : "Pre-register Guest"}
          </Button>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="manager-stats">
        <Card className="p-4 border border-border">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Members</p>
          <p className="text-2xl font-bold">{stats?.member_count || 0}<span className="text-sm font-normal text-muted-foreground">/{stats?.member_quota || 0}</span></p>
        </Card>
        <Card className="p-4 border border-border">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Slots Left</p>
          <p className="text-2xl font-bold text-orange-500">{(stats?.member_quota || 0) - (stats?.member_count || 0)}</p>
        </Card>
        <Card className="p-4 border border-border">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Bookings</p>
          <p className="text-2xl font-bold">{stats?.total_bookings || 0}</p>
        </Card>
        <Card className="p-4 border border-border">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Active</p>
          <p className="text-2xl font-bold text-green-600">{stats?.active_bookings || 0}</p>
        </Card>
      </div>

      <Tabs defaultValue="members" className="space-y-6">
        <TabsList className="bg-muted rounded-none">
          <TabsTrigger value="members" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background" data-testid="tab-members">
            <Users className="h-4 w-4 mr-2" />Team Members
          </TabsTrigger>
          <TabsTrigger value="bookings" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background" data-testid="tab-bookings">
            <CalendarDays className="h-4 w-4 mr-2" />Rentals
          </TabsTrigger>
          <TabsTrigger value="messages" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background" data-testid="tab-messages">
            <MessageSquare className="h-4 w-4 mr-2" />Messages
            {inbox.filter(m => !m.read).length > 0 && <Badge className="ml-2 bg-orange-500 text-white text-xs h-5 w-5 p-0 flex items-center justify-center rounded-full">{inbox.filter(m => !m.read).length}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* Members Tab */}
        <TabsContent value="members" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-xl">Team Members</h2>
            <Button onClick={() => setShowAddMember(true)} className="bg-foreground text-background rounded-none text-sm" data-testid="add-member-btn">
              <Plus className="h-4 w-4 mr-1" />Add Member
            </Button>
          </div>
          {members.length === 0 ? (
            <Card className="p-8 text-center border border-border">
              <Users className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No team members yet. Add your first member.</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {members.map(m => (
                <Card key={m.id} className="p-4 border border-border flex items-center justify-between" data-testid={`member-${m.id}`}>
                  <div>
                    <p className="font-medium">{m.name}</p>
                    <p className="text-sm text-muted-foreground">{m.email}</p>
                  </div>
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={() => removeMember(m.id)} data-testid={`remove-member-${m.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </Card>
              ))}
            </div>
          )}

          {/* Add Member Dialog */}
          <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
            <DialogContent data-testid="add-member-dialog">
              <DialogHeader>
                <DialogTitle>Add Team Member</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div><Label>Name *</Label><Input value={newMember.name} onChange={e => setNewMember(p => ({ ...p, name: e.target.value }))} placeholder="Employee name" data-testid="new-member-name" /></div>
                <div><Label>Email *</Label><Input type="email" value={newMember.email} onChange={e => setNewMember(p => ({ ...p, email: e.target.value }))} placeholder="employee@company.com" data-testid="new-member-email" /></div>
                <div><Label>Phone</Label><Input value={newMember.phone} onChange={e => setNewMember(p => ({ ...p, phone: e.target.value }))} placeholder="Optional" /></div>
                <p className="text-xs text-muted-foreground">A secure password will be auto-generated and emailed to the member.</p>
                <p className="text-xs text-muted-foreground">This member will inherit your company plan: <strong>{user?.plan_type || "standard"}</strong></p>
                <Button onClick={addMember} className="w-full bg-foreground text-background rounded-none" data-testid="confirm-add-member-btn">
                  Add Member
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Bookings Tab */}
        <TabsContent value="bookings" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-xl">Recent Rentals</h2>
            <div className="flex gap-1 border rounded-lg p-0.5" data-testid="rentals-view-toggle">
              <Button
                variant={rentalsView === "list" ? "default" : "ghost"}
                size="sm"
                className={`h-7 px-2.5 rounded-md ${rentalsView === "list" ? "bg-foreground text-background" : ""}`}
                onClick={() => setRentalsView("list")}
                data-testid="rentals-list-view"
              >
                <List className="h-3.5 w-3.5 mr-1" />List
              </Button>
              <Button
                variant={rentalsView === "calendar" ? "default" : "ghost"}
                size="sm"
                className={`h-7 px-2.5 rounded-md ${rentalsView === "calendar" ? "bg-foreground text-background" : ""}`}
                onClick={() => setRentalsView("calendar")}
                data-testid="rentals-calendar-view"
              >
                <CalendarDays className="h-3.5 w-3.5 mr-1" />Calendar
              </Button>
            </div>
          </div>

          {bookings.length === 0 ? (
            <Card className="p-8 text-center border border-border">
              <CalendarDays className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No bookings yet.</p>
            </Card>
          ) : rentalsView === "list" ? (
            <div className="space-y-2">
              {bookings.map(b => (
                <Card key={b.id} className="p-4 border border-border" data-testid={`booking-${b.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-sm">{b.space_name}</p>
                        <Badge className={`text-xs rounded-sm ${statusColor(b.status)}`}>{b.status}</Badge>
                      </div>
                      {b.meeting_name && <p className="text-xs text-muted-foreground mb-0.5">{b.meeting_name}</p>}
                      {b.booked_for_name && <p className="text-xs text-rig-teal mb-0.5">For: {b.booked_for_name}</p>}
                      <p className="text-xs text-muted-foreground">
                        By <span className="font-medium text-foreground">{b.user_name}</span> ({b.user_email})
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-mono">{b.start_datetime?.slice(0, 10)}</p>
                      <p className="text-xs text-muted-foreground">{b.start_datetime?.slice(11, 16)} - {b.end_datetime?.slice(11, 16)}</p>
                      <p className="text-sm font-bold mt-1">XCG {b.price?.toFixed(2)}</p>
                    </div>
                    {!["cancelled", "completed", "pending_cancellation"].includes(b.status) && (
                      <div className="flex gap-1 sm:ml-2">
                        <Button variant="ghost" size="sm" onClick={() => setEditBooking(b)} data-testid={`edit-booking-${b.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => cancelBooking(b.id)} data-testid={`cancel-booking-${b.id}`}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <BookingsCalendar
              bookings={bookings}
              calMonth={calMonth}
              setCalMonth={setCalMonth}
              statusColor={statusColor}
              onEdit={setEditBooking}
              onCancel={cancelBooking}
            />
          )}
        </TabsContent>

        {/* Messages Tab */}
        <TabsContent value="messages" className="space-y-4">
          {/* Admin Inbox */}
          {inbox.length > 0 && (
            <>
              <h2 className="font-heading font-semibold text-xl">From Admin</h2>
              <div className="space-y-3 mb-6" data-testid="admin-inbox">
                {inbox.map(m => (
                  <Card key={m.id} className={`p-4 border ${!m.read ? "border-orange-300 bg-orange-50/30" : "border-border"}`} data-testid={`inbox-msg-${m.id}`}>
                    <div className="flex items-start justify-between mb-1">
                      <span className="font-medium text-sm">{m.subject}</span>
                      <div className="flex items-center gap-2">
                        {!m.read && <Badge className="bg-orange-500 text-white text-xs">New</Badge>}
                        <span className="text-xs text-muted-foreground">{new Date(m.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground whitespace-pre-line">{m.content}</p>
                    <p className="text-xs text-muted-foreground mt-2">From: {m.sender_name}</p>
                    {!m.read && (
                      <Button size="sm" variant="ghost" className="text-xs mt-1"
                        onClick={async () => { try { await api("put", `/messages/inbox/${m.id}/read`); loadAll(); } catch {} }}
                        data-testid={`mark-read-${m.id}`}
                      >Mark as read</Button>
                    )}
                  </Card>
                ))}
              </div>
              <Separator />
            </>
          )}

          <h2 className="font-heading font-semibold text-xl">Messages to Admin</h2>

          <Card className="p-4 border border-border" data-testid="message-form">
            <div className="space-y-3">
              <div className="flex gap-3">
                <Select value={msgForm.message_type} onValueChange={v => setMsgForm(p => ({ ...p, message_type: v }))}>
                  <SelectTrigger className="w-40" data-testid="message-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="note">Note</SelectItem>
                    <SelectItem value="complaint">Complaint</SelectItem>
                    <SelectItem value="request">Request</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Textarea
                value={msgForm.content}
                onChange={e => setMsgForm(p => ({ ...p, content: e.target.value }))}
                placeholder="Type your message to admin..."
                rows={3}
                data-testid="message-content"
              />
              <Button onClick={sendMessage} disabled={sending} className="bg-foreground text-background rounded-none text-sm" data-testid="send-message-btn">
                <Send className="h-4 w-4 mr-1" />{sending ? "Sending..." : "Send Message"}
              </Button>
            </div>
          </Card>

          <Separator />

          {messages.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No messages yet.</p>
          ) : (
            <div className="space-y-3">
              {messages.map(m => (
                <Card key={m.id} className="p-4 border border-border" data-testid={`message-${m.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <Badge variant="outline" className="text-xs">{m.message_type}</Badge>
                    <span className="text-xs text-muted-foreground">{new Date(m.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-sm mb-2">{m.content}</p>
                  {m.reply ? (
                    <div className="bg-muted/50 rounded p-3 mt-2 border-l-2 border-green-500">
                      <p className="text-xs text-muted-foreground mb-1 font-medium">Admin Reply:</p>
                      <p className="text-sm">{m.reply}</p>
                      <p className="text-xs text-muted-foreground mt-1">{new Date(m.replied_at).toLocaleDateString()}</p>
                    </div>
                  ) : (
                    <p className="text-xs text-amber-600 flex items-center gap-1"><Clock className="h-3 w-3" />Awaiting response</p>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      <EditBookingDialog
        booking={editBooking}
        open={!!editBooking}
        onClose={() => setEditBooking(null)}
        api={api}
        onSaved={loadAll}
      />
    </div>
  );
}